"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";

import { isChunkLoadError } from "@/lib/runtime/chunk-load-error";
import { reportNextjsError } from "@/lib/datadog/rum";

type Props = {
  children: ReactNode;
};

type State = {
  chunkError: boolean;
};

/**
 * Catches stale chunk failures after deploy and offers a safe full reload.
 */
export class ChunkLoadRecovery extends Component<Props, State> {
  state: State = { chunkError: false };

  static getDerivedStateFromError(error: Error): State | null {
    if (isChunkLoadError(error)) return { chunkError: true };
    return null;
  }

  componentDidCatch(error: Error, _info: ErrorInfo): void {
    if (isChunkLoadError(error)) {
      reportNextjsError(error);
    }
  }

  private reload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.chunkError) {
      return (
        <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 px-6 text-center">
          <p className="max-w-md text-sm text-muted-foreground">
            A new version of Inora was deployed. Refresh to load the latest
            files.
          </p>
          <button
            type="button"
            onClick={this.reload}
            className="rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background"
          >
            Refresh page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
